import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DatePickerSlotProps } from '@mui/x-date-pickers/DatePicker/DatePicker.types';
import { TimePicker, TimePickerSlotProps } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs, UnitType } from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, ControllerFieldState, useForm } from 'react-hook-form';

export type DateRangeValue = { from: Dayjs; to: Dayjs };

export type DateRangeProps = {
  onChange: (dateRange: DateRangeValue) => void;
  value?: DateRangeValue;
};

type Fields = {
  startDate: Dayjs;
  startTime: Dayjs;
  endDate: Dayjs;
  endTime: Dayjs;
  isAllDay: boolean;
};

export const DateRange = ({ onChange, value }: DateRangeProps) => {
  const today = useMemo(() => dayjs(), []);

  // `useForm` to maintain internal state of the component. It doesn't submit anything.
  const { control, watch, trigger, setValue } = useForm<Fields>({
    defaultValues: {
      startDate: value?.from || today,
      startTime: value?.from || today,
      endDate: value?.to || today,
      endTime: value?.to || today,
      isAllDay: isAllDay(value),
    },
    mode: 'all',
  });

  const fields = watch();

  useEffect(() => {
    void trigger();
    onChange(buildDateRange(fields));
  }, [trigger, fields, onChange]);

  const onStartDateChange = useCallback(
    (originalFn: (a: Dayjs | null) => void) => (value: Dayjs | null) => {
      // When `startDate` is changed to be greater that `endDate` (invalid state), we update `endDate` automatically
      // to equal `startDate`.
      if (value?.isAfter(fields.endDate)) {
        setValue('endDate', value);
      }
      originalFn(value);
    },
    [fields.endDate, setValue],
  );

  const validate = useCallback(
    (fieldName: Exclude<keyof Fields, 'isAllDay'>) => (value: Dayjs, formFields: Fields) => {
      switch (fieldName) {
        case 'endDate':
          if (value.isBefore(formFields.startDate)) {
            return 'End date must be after start date';
          }
          break;
        case 'endTime':
          if (value.isBefore(formFields.startTime)) {
            return 'End time must be after start time';
          }
          break;
      }
      return true;
    },
    [],
  );

  const commonDatePickerSlotsProps = useCallback(
    (fieldState: ControllerFieldState): DatePickerSlotProps<Dayjs, false> => ({
      textField: {
        error: !!fieldState.error,
      },
    }),
    [],
  );

  const commonTimePickerSlotsProps = useCallback(
    (fieldState: ControllerFieldState): TimePickerSlotProps<Dayjs, false> => ({
      textField: {
        error: !!fieldState.error,
      },
    }),
    [],
  );

  return (
    <Stack direction="row" spacing={1}>
      <Stack spacing={1}>
        <Controller
          name="startDate"
          control={control}
          render={({ field, fieldState }) => (
            <DatePicker
              {...field}
              onChange={onStartDateChange(field.onChange)}
              slots={{ textField: TextField }}
              slotProps={commonDatePickerSlotsProps(fieldState)}
            />
          )}
        />
        {!fields.isAllDay && (
          <Controller
            name="startTime"
            control={control}
            render={({ field, fieldState }) => (
              <TimePicker
                {...field}
                slots={{ textField: TextField }}
                slotProps={commonTimePickerSlotsProps(fieldState)}
                ampm={false}
              />
            )}
          />
        )}
        <Controller
          name="isAllDay"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label="All day"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(_, checked) => field.onChange(checked)}
                />
              }
            />
          )}
        />
      </Stack>
      <Stack spacing={1}>
        <Box>
          <Typography sx={{ marginTop: '1rem' }}>to</Typography>
        </Box>
      </Stack>
      <Stack spacing={1}>
        <Controller
          name="endDate"
          control={control}
          rules={{
            validate: validate('endDate'),
          }}
          render={({ field, fieldState }) => (
            <DatePicker
              {...field}
              slots={{ textField: TextField }}
              slotProps={commonDatePickerSlotsProps(fieldState)}
            />
          )}
        />
        {!fields.isAllDay && (
          <Controller
            name="endTime"
            control={control}
            rules={{
              validate: validate('endTime'),
            }}
            render={({ field, fieldState }) => (
              <TimePicker
                {...field}
                slots={{ textField: TextField }}
                slotProps={commonTimePickerSlotsProps(fieldState)}
                ampm={false}
              />
            )}
          />
        )}
      </Stack>
    </Stack>
  );
};

function updateDateWith(date: Dayjs, nextDate: Dayjs, units: UnitType[] = []): Dayjs {
  return units.reduce((acc, unit) => acc.set(unit, nextDate.get(unit)), date);
}

function setTimePart(date: Dayjs, time: Dayjs): Dayjs {
  return updateDateWith(date, time, ['h', 'm', 's']);
}

function buildDateRange({
  startDate,
  startTime,
  endDate,
  endTime,
  isAllDay,
}: Fields): DateRangeValue {
  if (isAllDay) {
    return { from: startDate.startOf('day'), to: endDate.endOf('day') };
  }
  return { from: setTimePart(startDate, startTime), to: setTimePart(endDate, endTime) };
}

function isAllDay(value: DateRangeValue | null | undefined) {
  return (
    value?.from?.isSame(value?.from?.startOf('day')) &&
    value?.to?.isSame(value?.to?.endOf('day'))
  );
}
